import { useCallback } from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { executeAiManagedObject as executeAiManagedObjectRequest } from '@atlassian/ai-managed-objects/api';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';
import { getExecutionLifecycleState } from '../../common/lifecycle';
import { findAgentManagedExtension, findAgentManagedExtensionByLocalId } from '../../common/utils';
import {
	dispatchAgentManagedExtensionMeta,
	replaceNodeBodyContent,
	setAgentManagedExtensionMeta,
} from '../editor-commands/agent-managed-object-node';
import { getCacheAiManagedObjectMeta } from '../editor-commands/configure-popup';

type UseAiManagedExecutionArgs = {
	editorView: EditorView;
};

type ExecuteAiManagedObjectArgs = {
	aimoId: string;
	localId?: string;
};

type UseAiManagedExecutionResult = {
	executeAiManagedObject: (args: ExecuteAiManagedObjectArgs) => Promise<AiManagedObject | null>;
};

let executionRequestSequence = 0;

const createExecutionRequestId = (): string => `execution-${++executionRequestSequence}`;

const getFinishExecutionMeta = ({ localId, requestId }: { localId?: string; requestId: string }) =>
	localId
		? {
				localId,
				requestId,
				type: 'FINISH_EXECUTION' as const,
			}
		: undefined;

export const useAiManagedExecution = ({
	editorView,
}: UseAiManagedExecutionArgs): UseAiManagedExecutionResult => {
	const startExecution = useCallback(
		({
			lifecycleState,
			localId,
			requestId,
		}: {
			lifecycleState: ReturnType<typeof getExecutionLifecycleState>;
			localId?: string;
			requestId: string;
		}): void => {
			if (!localId) {
				return;
			}

			dispatchAgentManagedExtensionMeta(editorView, {
				lifecycleState,
				localId,
				requestId,
				type: 'START_EXECUTION',
			});
		},
		[editorView],
	);

	const getCurrentLifecycleState = useCallback(
		(localId?: string): ReturnType<typeof getExecutionLifecycleState> => {
			const found =
				findAgentManagedExtensionByLocalId(editorView.state, localId) ??
				findAgentManagedExtension(editorView.state);

			return found ? getExecutionLifecycleState(found.node) : 'no-body-executing';
		},
		[editorView],
	);

	const isLatestExecutionRequest = useCallback(
		({ localId, requestId }: { localId?: string; requestId: string }): boolean => {
			if (!localId) {
				return true;
			}

			const pluginState = pluginKey.getState(editorView.state);
			if (!pluginState) {
				return true;
			}

			return pluginState.executionLifecycleByLocalId[localId]?.requestId === requestId;
		},
		[editorView],
	);

	const executeAiManagedObject = useCallback(
		async ({ aimoId, localId }: ExecuteAiManagedObjectArgs): Promise<AiManagedObject | null> => {
			const requestId = createExecutionRequestId();
			const lifecycleState = getCurrentLifecycleState(localId);
			startExecution({ lifecycleState, localId, requestId });
			try {
				const executionResult = await executeAiManagedObjectRequest(aimoId);

				if (!isLatestExecutionRequest({ localId, requestId })) {
					return null;
				}

				const executeTr = editorView.state.tr;
				replaceNodeBodyContent(
					editorView.state,
					executeTr,
					executionResult.output.blockContent,
					localId,
				);
				const cacheAiManagedObjectMeta = getCacheAiManagedObjectMeta(
					executionResult.aiManagedObject,
					localId,
				);
				const finishExecutionMeta = getFinishExecutionMeta({ localId, requestId });
				setAgentManagedExtensionMeta(
					executeTr,
					finishExecutionMeta
						? [cacheAiManagedObjectMeta, finishExecutionMeta]
						: cacheAiManagedObjectMeta,
				);

				editorView.dispatch(executeTr);

				return executionResult.aiManagedObject;
			} catch {
				if (isLatestExecutionRequest({ localId, requestId }) && localId) {
					const failureTr = editorView.state.tr;
					setAgentManagedExtensionMeta(failureTr, [
						{
							localId,
							requestId,
							type: 'FAIL_EXECUTION',
						},
						{
							localId,
							requestId,
							type: 'FINISH_EXECUTION',
						},
					]);
					editorView.dispatch(failureTr);
				}
				return null;
			}
		},
		[editorView, getCurrentLifecycleState, isLatestExecutionRequest, startExecution],
	);

	return { executeAiManagedObject };
};
